import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider, useSession } from "@/auth/SessionProvider";
import { AppShell } from "@/components/AppShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingState } from "@/components/States";
import { ToastProvider } from "@/components/ui/toast";
import { AccountPage } from "@/pages/AccountPage";
import { ChatPage } from "@/pages/ChatPage";
import { CheckoutCancelledPage } from "@/pages/CheckoutCancelledPage";
import { CheckoutSuccessPage } from "@/pages/CheckoutSuccessPage";
import { ClaimOrgsPage } from "@/pages/ClaimOrgsPage";
import { ContactsPage } from "@/pages/ContactsPage";
import { ContactDossierPage } from "@/pages/contacts/ContactDossierPage";
import { OperatorAccessScreen, OperatorCatalogueScreen, OperatorOrganisationsScreen } from "@/pages/OperatorPage";
import { OperatorConsumptionScreen } from "@/pages/operator/ConsumptionPage";
import { OperatorDossierScreen } from "@/pages/operator/OrganisationDossier";
import { HighIntentPage } from "@/pages/HighIntentPage";
import { CampaignsBoard } from "@/pages/highIntent/CampaignsBoard";
import {
  CampaignDetailPage,
  CampaignAudienceTab,
  CampaignDeliveryTab,
  CampaignImportTab,
  CampaignOverviewTab,
  CampaignReportsTab,
  CampaignRunsTab,
} from "@/pages/highIntent/CampaignDetailPage";
import { DeliveryPage } from "@/pages/highIntent/DeliveryPage";
import { LeadsSearch } from "@/pages/highIntent/LeadsSearch";
import { SettingsPanel } from "@/pages/highIntent/SettingsPanel";
import { ProspectingPage } from "@/pages/ProspectingPage";
import { SignInPage } from "@/pages/SignInPage";
import { SiteDetailPage } from "@/pages/SiteDetailPage";
import { SitesPage } from "@/pages/SitesPage";
import { SuppressionPage } from "@/pages/SuppressionPage";
import { isApiError } from "@/lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx from the api function is a decision, not a blip: an expired quote, a
      // suppressed contact, a missing membership. Retrying one wastes the customer's time
      // and, in the money path, muddies which attempt actually happened.
      retry: (failureCount, error) => {
        if (isApiError(error) && error.httpStatus >= 400 && error.httpStatus < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: { retry: false },
  },
});

/** Exported for the claim-routing tests (src/test/claimOrgs.test.tsx); production renders it only through App below. */
export function AuthedRoutes() {
  const { session, loading, memberships, membershipsLoaded, membershipsError, isSuperAdmin } = useSession();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Checking your session…" />
      </div>
    );
  }

  if (!session) return <SignInPage />;

  // W6-03-B: hold the shell until memberships have actually been read. Routing on an
  // unknown membership state would mount an org-scoped surface for a frame with no org
  // context at all - and would flash the claim page for members (or the shell for
  // claimants). A load error falls through to the shell, which renders today's dead-end
  // copy rather than wrongly offering a claim.
  if (!membershipsLoaded && !membershipsError) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label="Loading your organisations…" />
      </div>
    );
  }

  // A signed-in user with no memberships cannot reach any org-scoped surface - every such
  // call answers 403 (resolveOrgScope). Route them to the claim page, which offers the
  // organisations their sign-in address approved, instead of the shell's dead end.
  if (!membershipsError && memberships.length === 0 && !isSuperAdmin) {
    return <ClaimOrgsPage />;
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<ChatPage />} />
        <Route path="prospecting" element={<ProspectingPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        {/* Story C4: the dossier as a deep-linkable page; the drawer remains the in-list
            quick view and links here. The static "contacts" route ranks apart from the
            dynamic "contacts/:contactId", so the list route is untouched. */}
        <Route path="contacts/:contactId" element={<ContactDossierPage />} />
        <Route path="sites" element={<SitesPage />} />
        <Route path="sites/:siteId" element={<SiteDetailPage />} />
        <Route path="suppression" element={<SuppressionPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="account/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="account/checkout/cancelled" element={<CheckoutCancelledPage />} />
        {/* Story P2-UI: the operator console is ONE SCREEN PER JOB on the DS Nav Rail.
            Billing is removed from the console entirely (the money ruling). */}
        <Route path="operator" element={<Navigate to="/operator/catalogue" replace />} />
        <Route path="operator/catalogue" element={<OperatorCatalogueScreen />} />
        <Route path="operator/access" element={<OperatorAccessScreen />} />
        <Route path="operator/organisations" element={<OperatorOrganisationsScreen />} />
        <Route path="operator/organisations/:orgId" element={<OperatorDossierScreen />} />
        {/* Story O10: the W-D4 read-only consumption and price book screen. GET reads
            only; the server's super-admin gate is the authority, this route is the door. */}
        <Route path="operator/consumption" element={<OperatorConsumptionScreen />} />
        <Route path="high-intent" element={<HighIntentPage />}>
          {/* Story N3 gated module; story P2-UI made it CAMPAIGN FIRST: the index is the
              campaigns board, each campaign has a detail route with Overview (the
              Campaign Reference content), Runs and Reports, and every screen stays
              deep-linkable. The subscription gate lives in HighIntentPage and wraps all
              screens. */}
          <Route index element={<CampaignsBoard />} />
          <Route path="campaigns/:campaignKey" element={<CampaignDetailPage />}>
            <Route index element={<CampaignOverviewTab />} />
            <Route path="runs" element={<CampaignRunsTab />} />
            <Route path="reports" element={<CampaignReportsTab />} />
            <Route path="delivery" element={<CampaignDeliveryTab />} />
            <Route path="audience" element={<CampaignAudienceTab />} />
            {/* Story O05: the manual CSV upload, one job per screen. */}
            <Route path="import" element={<CampaignImportTab />} />
          </Route>
          <Route path="leads" element={<LeadsSearch />} />
          <Route path="delivery" element={<DeliveryPage />} />
          <Route path="settings" element={<SettingsPanel />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <ToastProvider>
            {/* Opted into the v7 behaviours now so the upgrade is a version bump, not a
                behaviour change: state updates wrapped in startTransition, and relative
                route resolution inside splat routes. */}
            <BrowserRouter
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <AuthedRoutes />
            </BrowserRouter>
          </ToastProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
